import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, ShieldCheck, BatteryCharging, Flame } from "lucide-react";
import { BRAND, buildWhatsAppLink, STATS, IMAGES } from "@/data/site";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center pt-28 pb-16 overflow-hidden bg-grain"
    >
      {/* Background image with gradients */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        <div className="absolute inset-0 spotlight-gold opacity-70" />
        <div className="absolute inset-0 spotlight-blue opacity-60" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center w-full">
        {/* Left content */}
        <div className="lg:col-span-7 z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-gold mb-6 animate-fade-up">
            <ShieldCheck className="w-3.5 h-3.5 text-gold" />
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-semibold">
              Yetkili İnci Akü Bayisi
            </span>
          </div>

          <h1
            className="font-display font-black leading-[0.95] text-balance animate-fade-up [animation-delay:80ms]"
            style={{ animationFillMode: "both" }}
          >
            <span className="block text-5xl sm:text-6xl lg:text-7xl text-gradient-silver">
              Güvenli Sürüş,
            </span>
            <span className="block text-5xl sm:text-6xl lg:text-7xl text-gradient-gold mt-2">
              Maksimum Performans.
            </span>
          </h1>

          <p
            className="mt-6 text-base sm:text-lg text-white/75 max-w-xl leading-relaxed animate-fade-up [animation-delay:160ms]"
            style={{ animationFillMode: "both" }}
          >
            Eskişehir Tepebaşı'nda LPG sistemleri ve orijinal İnci Akü
            servisi. Bilgisayarlı LPG ayarı, sıralı sistem servisi, akü test &
            montaj — her şey tek elden, garanti ile.
          </p>

          <div
            className="mt-9 flex flex-col sm:flex-row gap-4 animate-fade-up [animation-delay:240ms]"
            style={{ animationFillMode: "both" }}
          >
            <Link
              to="/iletisim"
              data-testid="hero-quote-cta"
              className="group gradient-gold text-black font-semibold px-7 py-4 inline-flex items-center gap-2 hover:shadow-[0_0_40px_rgba(212,175,55,0.5)] transition-all"
            >
              Hızlı Teklif Al
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-cta"
              className="bg-[#25D366] hover:bg-[#1ebd5a] text-white font-semibold px-7 py-4 inline-flex items-center gap-2 transition-all"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp ile İletişim
            </a>
          </div>

          {/* Stats */}
          <div
            className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 max-w-2xl animate-fade-up [animation-delay:320ms]"
            style={{ animationFillMode: "both" }}
          >
            {STATS.map((s) => (
              <div key={s.label} className="border-l-2 border-gold/60 pl-4">
                <div className="font-display text-3xl sm:text-4xl font-bold text-white">
                  {s.value}
                </div>
                <div className="text-xs uppercase tracking-[0.18em] text-white/60 mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right glass card */}
        <div className="lg:col-span-5 z-10 hidden lg:block">
          <div
            className="relative glass-strong rounded-2xl p-8 animate-fade-up [animation-delay:200ms]"
            style={{ animationFillMode: "both" }}
          >
            <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-gold/20 via-transparent to-inci-blue/20 -z-10 blur-xl" />

            <div className="flex items-center gap-4 mb-8">
              <img
                src={BRAND.logo}
                alt="Star Otogaz"
                className="h-20 w-auto drop-shadow-[0_0_20px_rgba(212,175,55,0.4)]"
              />
              <div>
                <div className="text-gold uppercase text-[10px] tracking-[0.3em] font-semibold">
                  Premium Servis
                </div>
                <div className="font-display font-bold text-lg leading-tight text-white">
                  Tek Elden Çözüm
                </div>
              </div>
            </div>

            <div className="space-y-3 mb-7">
              {[
                { icon: Flame, label: "LPG Montajı & Servisi", sub: "TSE onaylı uygulama" },
                { icon: BatteryCharging, label: "İnci Akü Satış", sub: "Orijinal • Garantili" },
                { icon: ShieldCheck, label: "Periyodik Bakım", sub: "30 noktalı kontrol" },
              ].map(({ icon: Icon, label, sub }) => (
                <div
                  key={label}
                  className="flex items-center gap-4 p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:border-gold/40 hover:bg-white/[0.04] transition-all"
                >
                  <div className="w-10 h-10 rounded-md bg-gold/10 border border-gold/30 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-gold" />
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm">{label}</div>
                    <div className="text-xs text-white/55">{sub}</div>
                  </div>
                </div>
              ))}
            </div>

            <a
              href={`tel:${BRAND.phoneTel}`}
              data-testid="hero-card-call"
              className="block text-center bg-inci-blue hover:bg-inci-blue-light text-white font-semibold py-3.5 transition-all"
            >
              Hemen Ara: {BRAND.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Bottom marquee */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-white/5 bg-black/40 backdrop-blur-md py-3 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap gap-12 text-xs text-white/50 uppercase tracking-[0.4em] font-semibold">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex gap-12">
              <span>★ TSE Onaylı LPG Uygulama</span>
              <span className="text-gold">/</span>
              <span>★ Yetkili İnci Akü Bayisi</span>
              <span className="text-gold">/</span>
              <span>★ 15+ Yıllık Tecrübe</span>
              <span className="text-gold">/</span>
              <span>★ Yazılı Garanti</span>
              <span className="text-gold">/</span>
              <span>★ Şeffaf Fiyatlandırma</span>
              <span className="text-gold">/</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
